import { FormDiscriminatedProps } from '@App/forms/common/form-field'
import { DropdownDiscriminatedProps } from '@App/forms/common/dropdown-form-field'
import { SelectOption } from '@Components/Selectors/Select'
import { MAX_LENGTH_MESSAGE, REQUIRED_MESSAGE } from '@Const/error-messages'
import { FormFieldTypes } from '@App/enum/form-field-types'
import {
    getDefaultAttention,
    getDefaultCity,
    getDefaultPostalCode,
    getDefaultState,
    getDefaultStreetAddress,
    getDefaultUnitNumber,
} from '@App/forms/common/mailing-address-form-defaults'

export interface MailingAddressFormDefinition {
    streetAddress: string
    unitNumber?: string
    attention?: string
    city: string
    state?: string
    countryId: string
    postalCode?: string
}

export interface MailingAddressFormProps {
    streetAddress: FormDiscriminatedProps
    unitNumber: FormDiscriminatedProps
    attention?: FormDiscriminatedProps
    city: FormDiscriminatedProps
    state: DropdownDiscriminatedProps | FormDiscriminatedProps
    country: DropdownDiscriminatedProps
    postalCode: FormDiscriminatedProps
}

export const getMailingAddressFormProps = (
    prefix = '',
    withAttention?: boolean,
    optional?: boolean
): MailingAddressFormProps => ({
    streetAddress: getDefaultStreetAddress(prefix, !!optional),
    unitNumber: getDefaultUnitNumber(prefix),
    attention: withAttention ? getDefaultAttention(prefix) : undefined,
    city: getDefaultCity(prefix, !!optional),
    state: getDefaultState(prefix, [], !!optional),
    country: {
        type: FormFieldTypes.DROPDOWN,
        name: prefix + 'countryId',
        label: 'Country',
        isClearable: true,
        registerOptions: {
            required: optional ? false : REQUIRED_MESSAGE,
        },
        options: [],
    },
    postalCode: getDefaultPostalCode(prefix, !!optional),
})

export const markFieldsAsRequired = (
    mailingAddressFormProps: MailingAddressFormProps,
    isOptional: boolean
): MailingAddressFormProps => {
    mailingAddressFormProps.streetAddress = {
        ...mailingAddressFormProps.streetAddress,
        registerOptions: {
            ...mailingAddressFormProps.streetAddress.registerOptions,
            required: isOptional ? false : REQUIRED_MESSAGE,
        },
    }
    mailingAddressFormProps.city = {
        ...mailingAddressFormProps.city,
        registerOptions: {
            ...mailingAddressFormProps.city.registerOptions,
            required: isOptional ? false : REQUIRED_MESSAGE,
        },
    }
    mailingAddressFormProps.country = {
        ...mailingAddressFormProps.country,
        registerOptions: {
            ...mailingAddressFormProps.country.registerOptions,
            required: isOptional ? false : REQUIRED_MESSAGE,
        },
    }
    if (isOptional) {
        mailingAddressFormProps.state = {
            ...mailingAddressFormProps.state,
            registerOptions: { ...mailingAddressFormProps.state.registerOptions, required: false },
        }
        mailingAddressFormProps.postalCode = {
            ...mailingAddressFormProps.postalCode,
            registerOptions: { ...mailingAddressFormProps.postalCode.registerOptions, required: false },
        }
    }

    return mailingAddressFormProps
}

export const modifyMailingAddressFormProps = (
    mailingAddressFormProps: MailingAddressFormProps,
    isPostalCodeRequired: boolean,
    isUSA: boolean,
    isCanada: boolean,
    stateOptions: SelectOption[]
): MailingAddressFormProps => {
    if (isUSA || isCanada) {
        mailingAddressFormProps.state = {
            ...mailingAddressFormProps.state,
            type: FormFieldTypes.DROPDOWN,
            options: stateOptions,
            registerOptions: { ...mailingAddressFormProps.state.registerOptions, required: REQUIRED_MESSAGE },
        }
    } else {
        mailingAddressFormProps.state = {
            ...mailingAddressFormProps.state,
            type: FormFieldTypes.TEXT,
            registerOptions: {
                ...mailingAddressFormProps.state.registerOptions,
                required: false,
                maxLength: { value: 60, message: `${MAX_LENGTH_MESSAGE} 60` },
            },
        }
    }

    if (isPostalCodeRequired) {
        mailingAddressFormProps.postalCode = {
            ...mailingAddressFormProps.postalCode,
            registerOptions: {
                ...mailingAddressFormProps.postalCode.registerOptions,
                required: REQUIRED_MESSAGE,
                maxLength: { value: 10, message: `${MAX_LENGTH_MESSAGE} 10` },
                validate: {
                    checkPostalCodeFormat: (value: string) => {
                        let regex
                        if (isUSA) {
                            regex = /(^\d{5}$)|(^\d{5}-\d{4}$)/
                        }
                        if (isCanada) {
                            regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
                        }
                        if (regex) {
                            return regex.test(value) ? true : 'Must be a valid ZIP code'
                        } else {
                            return !!value
                        }
                    },
                },
            },
        }
    } else {
        mailingAddressFormProps.postalCode = {
            ...mailingAddressFormProps.postalCode,
            registerOptions: {
                ...mailingAddressFormProps.postalCode.registerOptions,
                required: false,
                maxLength: { value: 60, message: `${MAX_LENGTH_MESSAGE} 60` },
                validate: undefined,
            },
        }
    }

    return mailingAddressFormProps
}
